// --- React
import React, { useCallback } from 'react';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';

/**
 * SaveForm Component
 *
 * This component is responsible for rendering the save confirmation message.
 * It displays a message to confirm the save action.
 *
 * @component
 * @param {Object} msg - The message object containing confirmation text.
 * @returns {JSX.Element} The rendered SaveForm component.
 */
const SaveForm = ({ msg }) => (
    <div className="flexBox">
        <Typography variant="T30">{msg[0]}</Typography>
        <Typography variant="T20">{msg[1]}</Typography>
    </div>
);

/**
 * useSaveDialog Hook
 *
 * This hook provides functionality to open a save confirmation dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openSaveDialog function.
 */
export const useSaveDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the save confirmation dialog
    const openSaveDialog = useCallback(
        (label, msg, onConfirm) => {
            openDialog({
                title: 'Confirm Save',
                content: <SaveForm msg={msg} />,
                closeAction: true,
                primaryAction: {
                    label,
                    action: () => handleSubmit(onConfirm),
                },
            });
        },
        [openDialog, handleSubmit],
    );

    return { openSaveDialog };
};
